import React, { createContext, useContext, useEffect, useState, ReactNode } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { AuthService } from '../services/AuthService';
import SessionTimeoutWarning from '../components/auth/SessionTimeoutWarning';

// User interface with enhanced role support
interface User {
  id: number;
  name: string;
  email: string;
  role: 'admin' | 'client' | 'project_manager' | 'project_manager_lead' | 'developer' | 'developer_lead';
  is_active: boolean;
  account_status: 'active' | 'inactive' | 'suspended';
  mfa_enabled: boolean;
  phone?: string;
  last_login_at?: string;
  created_at: string;
  updated_at: string;
}

// Auth context interface with enhanced functionality
interface AuthContextType {
  user: User | null;
  token: string | null;
  permissions: string[];
  loading: boolean;
  isAuthenticated: boolean;
  login: (email: string, password: string) => Promise<void>;
  loginWithToken: (token: string, user: User) => Promise<void>;
  register: (userData: RegisterData) => Promise<any>;
  logout: () => Promise<void>;
  refreshToken: () => Promise<void>;
  changePassword: (currentPassword: string, newPassword: string, confirmPassword: string) => Promise<void>;
  hasRole: (role: string) => boolean;
  hasAnyRole: (roles: string[]) => boolean;
  hasPermission: (permission: string) => boolean;
  hasAnyPermission: (permissions: string[]) => boolean;
  isAdmin: () => boolean;
  isClient: () => boolean;
  isProjectManager: () => boolean;
  isDeveloper: () => boolean;
  isLeadRole: () => boolean;
  getRoleHierarchy: () => number;
  getDashboardPath: () => string;
}

// Role hierarchy mapping (higher number = higher privilege)
const ROLE_HIERARCHY = {
  'client': 1,
  'developer': 2,
  'project_manager': 3,
  'developer_lead': 4,
  'project_manager_lead': 5,
  'admin': 6
} as const;

// Register data interface for client company registration
interface RegisterData {
  firstName: string;
  lastName: string;
  email: string;
  password: string;
  password_confirmation: string;
  company: string;
  website?: string;
  industry?: string;
  phone?: string;
}

// Create auth context
const AuthContext = createContext<AuthContextType | undefined>(undefined);

// Auth provider props
interface AuthProviderProps {
  children: ReactNode;
}

// Auth provider component
export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [token, setToken] = useState<string | null>(null);
  const [permissions, setPermissions] = useState<string[]>([]);
  const [loading, setLoading] = useState(true);

  // Initialize auth state on component mount
  useEffect(() => {
    initializeAuth();
  }, []);

  const initializeAuth = async () => {
    try {
      const storedToken = AuthService.getToken();
      const storedUser = AuthService.getUser();

      if (storedToken && storedUser) {
        setToken(storedToken);
        setUser(storedUser);
        
        // Trust stored credentials on page load without verification to prevent logout on refresh
        // Token will be verified on next API call and automatically clear if invalid
        const storedPermissions = AuthService.getPermissions();
        setPermissions(storedPermissions);
        
        console.log('Auth initialized from localStorage');
      }
    } catch (error) {
      console.error('Auth initialization error:', error);
      // Don't logout on initialization error - just leave user logged out
      setToken(null);
      setUser(null);
      setPermissions([]);
    } finally {
      setLoading(false);
    }
  };

  const login = async (email: string, password: string) => {
    try {
      setLoading(true);
      console.log('AuthContext: Attempting login for', email);
      const response = await AuthService.login({ email, password });
      console.log('AuthContext: Login successful, setting token and user');
      setToken(response.token);
      setUser(response.user);
      
      // Fetch permissions after successful login
      try {
        const profileResponse = await AuthService.getProfile();
        setPermissions(profileResponse.permissions || []);
        console.log('AuthContext: Permissions loaded:', profileResponse.permissions?.length || 0);
      } catch (error) {
        console.warn('Failed to fetch permissions:', error);
        setPermissions([]);
      }
    } catch (error: any) {
      const errorInfo = {
        message: error?.message,
        responseData: error?.response?.data,
        status: error?.response?.status,
        statusText: error?.response?.statusText
      };
      console.error('AuthContext: Login failed', JSON.stringify(errorInfo, null, 2));
      throw error;
    } finally {
      setLoading(false);
    }
  };

  const loginWithToken = async (token: string, user: User) => {
    try {
      setLoading(true);
      setToken(token);
      setUser(user);
      
      // Fetch permissions after setting token and user
      try {
        const profileResponse = await AuthService.getProfile();
        setPermissions(profileResponse.permissions || []);
      } catch (error) {
        console.warn('Failed to fetch permissions:', error);
        setPermissions([]);
      }
    } catch (error) {
      throw error;
    } finally {
      setLoading(false);
    }
  };

  const register = async (userData: RegisterData) => {
    try {
      setLoading(true);
      const response = await AuthService.register(userData);
      setToken(response.token);
      setUser(response.user);
      
      // Fetch permissions after successful registration
      try {
        const profileResponse = await AuthService.getProfile();
        setPermissions(profileResponse.permissions || []);
      } catch (error) {
        console.warn('Failed to fetch permissions:', error);
        setPermissions([]);
      }

      return response; // Return response so the component can handle redirect
    } catch (error) {
      throw error;
    } finally {
      setLoading(false);
    }
  };

  const logout = async () => {
    try {
      setLoading(true);
      await AuthService.logout();
    } catch (error) {
      console.error('Logout error:', error);
    } finally {
      setToken(null);
      setUser(null);
      setPermissions([]);
      setLoading(false);
    }
  };

  const refreshToken = async () => {
    try {
      const response = await AuthService.refreshToken();
      setToken(response.token);
    } catch (error) {
      await logout();
      throw error;
    }
  };

  const changePassword = async (currentPassword: string, newPassword: string, confirmPassword: string) => {
    try {
      await AuthService.changePassword({
        current_password: currentPassword,
        password: newPassword,
        password_confirmation: confirmPassword
      });
    } catch (error) {
      throw error;
    }
  };

  // Role checking methods
  const hasRole = (role: string): boolean => {
    return user?.role === role;
  };

  const hasAnyRole = (roles: string[]): boolean => {
    return user ? roles.includes(user.role) : false;
  };

  // Permission checking methods
  const hasPermission = (permission: string): boolean => {
    return permissions.includes(permission);
  };

  const hasAnyPermission = (permissionList: string[]): boolean => {
    return permissionList.some(permission => permissions.includes(permission));
  };

  // Role hierarchy methods
  const getRoleHierarchy = (): number => {
    return user ? ROLE_HIERARCHY[user.role] || 0 : 0;
  };

  const isAdmin = (): boolean => hasRole('admin');
  const isClient = (): boolean => hasRole('client');
  const isProjectManager = (): boolean => hasRole('project_manager') || hasRole('project_manager_lead');
  const isDeveloper = (): boolean => hasRole('developer') || hasRole('developer_lead');
  const isLeadRole = (): boolean => hasRole('developer_lead') || hasRole('project_manager_lead') || hasRole('admin');

  // Get the appropriate dashboard path for the user's role
  const getDashboardPath = (): string => {
    if (!user) return '/dashboard/client';
    
    switch (user.role) {
      case 'admin':
        return '/dashboard/admin';
      case 'project_manager':
      case 'project_manager_lead':
        return '/dashboard/pm';
      case 'developer':
      case 'developer_lead':
        return '/dashboard/developer';
      case 'client':
      default:
        return '/dashboard/client';
    }
  };

  const isAuthenticated = !!token && !!user;

  const contextValue: AuthContextType = {
    user,
    token,
    permissions,
    loading,
    isAuthenticated,
    login,
    loginWithToken,
    register,
    logout,
    refreshToken,
    changePassword,
    hasRole,
    hasAnyRole,
    hasPermission,
    hasAnyPermission,
    isAdmin,
    isClient,
    isProjectManager,
    isDeveloper,
    isLeadRole,
    getRoleHierarchy,
    getDashboardPath,
  };

  return (
    <AuthContext.Provider value={contextValue}>
      {children}
      {/* Session timeout warning for authenticated users */}
      {isAuthenticated && <SessionTimeoutWarning />}
    </AuthContext.Provider>
  );
};

// Hook to use auth context
export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

// HOC for route protection with enhanced role checking
interface ProtectedRouteProps {
  children: ReactNode;
  allowedRoles?: string[];
  requiredPermissions?: string[];
  minRoleLevel?: number;
  fallback?: ReactNode;
  loginRedirect?: boolean;
}

export const ProtectedRoute: React.FC<ProtectedRouteProps> = ({
  children,
  allowedRoles = [],
  requiredPermissions = [],
  minRoleLevel,
  fallback = <div>Access Denied</div>,
  loginRedirect = true,
}) => {
  const { 
    isAuthenticated, 
    hasAnyRole, 
    hasAnyPermission, 
    getRoleHierarchy, 
    loading 
  } = useAuth();
  const location = useLocation();

  if (loading) {
    return <div>Loading...</div>;
  }

  if (!isAuthenticated) {
    if (loginRedirect) {
      // Redirect to login with the current location as state so we can redirect back
      return <Navigate to="/login" state={{ from: location }} replace />;
    }
    return <div>Please log in to access this page</div>;
  }

  // Check role-based access
  if (allowedRoles.length > 0 && !hasAnyRole(allowedRoles)) {
    return <>{fallback}</>;
  }

  // Check permission-based access
  if (requiredPermissions.length > 0 && !hasAnyPermission(requiredPermissions)) {
    return <>{fallback}</>;
  }

  // Check minimum role level
  if (minRoleLevel !== undefined && getRoleHierarchy() < minRoleLevel) {
    return <>{fallback}</>;
  }

  return <>{children}</>;
};

// Export types
export type { User, AuthContextType };
export default AuthContext;